import {
  ArrowPathIcon,
  CalendarIcon,
  ClockIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  PaperClipIcon,
  ShieldExclamationIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReportAmendment,
  useReportCancel,
  useReportDelete,
} from "../../api/ReportsApi";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../../shared-components/ConfirmationDialog";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import useRegeneratePDFWithToast from "../../reports/PDFRegenerationWrapper";
import useNavigateNewPerson from "../../reports/useNavigateNewPerson";
import { ReportSummaryItemApi } from "../../api/Reports";
import ReactTooltip from "react-tooltip";
import { Modal } from "../../shared-components/Modal";
import { ChangeDueDateContainer } from "../../reports/ChangeDueDateContainer";
import { useUpdateMisconductDueDate } from "../../api/WorkflowApi";

type ReportUUIDProps = {
  reportUUID: string;
  text?: string;
  close?: () => void;
};

type AdminCompletedActionRowProps = {
  onClick: () => void;
  children: React.ReactNode;
};

function AdminCompletedActionRow({
  onClick,
  children,
}: AdminCompletedActionRowProps) {
  return (
    <div
      className="rounded-md p-1 cursor-pointer flex gap-2 items-center hover:bg-gray-100"
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export const ReportDeleteAdminCompletedButton = ({
  reportUUID,
  text,
  close,
}: ReportUUIDProps) => {
  const navigate = useNavigate();
  const {
    mutate: reportDelete,
    error: errorReports,
    reset,
    isSuccess,
    isLoading,
  } = useReportDelete();
  const { modal, show, hide } = useConfirmationDialog();

  function handleDelete(e: React.MouseEvent) {
    e.preventDefault();

    if (!reportUUID) {
      show(
        "Wrong Report UUID",
        "",
        () => {},
        <>The report is in an invalid state</>,
        false
      );
    }

    show(
      "Report Removal Confirmation",
      "Are you sure you want to remove the report?",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          reportDelete(reportUUID);
        }
        hide();
        close?.();
      }
    );
  }

  useEffect(() => {
    if (errorReports) {
      show(
        "Error Removing the report:",
        "",
        () => {},
        <div className="my-2">
          <ErrorMessageContainer>
            <>
              <strong className="block py-1">{reportUUID}</strong>
              Please try again or contact the administrator.
            </>
          </ErrorMessageContainer>
        </div>,
        false
      );
      reset();
    }
  }, [errorReports, reportUUID, reset, show]);

  useEffect(() => {
    if (isLoading) {
      show("", "", () => {}, <LoadingSpinner />, false);
    }
    if (isSuccess) {
      navigate(0);
    }
  }, [show, isSuccess, navigate, isLoading]);

  if (text) {
    return (
      <>
        <div
          className="text-red-500 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
          onClick={handleDelete}
        >
          <TrashIcon className="w-6" />
          {text}
        </div>
        {modal}
      </>
    );
  }

  return (
    <>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-delete-report"}
      />
      <TrashIcon
        className="w-6 text-red-500 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
        onClick={handleDelete}
        data-tip="Delete Report"
        data-for="rt-delete-report"
      />
      {modal}
    </>
  );
};

export const ReportAdminCancelButton = ({
  reportUUID,
  text,
  close,
}: ReportUUIDProps) => {
  const navigate = useNavigate();
  const {
    mutate: reportCancel,
    error: errorReports,
    reset,
    isSuccess,
    isLoading,
  } = useReportCancel();
  const { modal, show, hide } = useConfirmationDialog();

  function handleCancel(e: React.MouseEvent) {
    e.preventDefault();

    if (!reportUUID) {
      show(
        "Wrong Report UUID",
        "",
        () => {},
        <>The report is in an invalid state</>,
        false
      );
    }

    show(
      "Report Cancellation Confirmation",
      "Are you sure you want to cancel the report?",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          reportCancel(reportUUID);
        }
        hide();
        close?.();
      }
    );
  }

  useEffect(() => {
    if (errorReports) {
      show(
        "Error Cancelling the report:",
        "",
        () => {},
        <div className="my-2">
          <ErrorMessageContainer>
            <>
              <strong className="block py-1">{reportUUID}</strong>
              Please try again or contact the administrator.
            </>
          </ErrorMessageContainer>
        </div>,
        false
      );
      reset();
    }
  }, [errorReports, reportUUID, reset, show]);

  useEffect(() => {
    if (isLoading) {
      show("", "", () => {}, <LoadingSpinner />, false);
    }
    if (isSuccess) {
      navigate(0);
    }
  }, [show, isSuccess, navigate, isLoading]);

  if (text) {
    return (
      <>
        <div
          className="text-red-500 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
          onClick={handleCancel}
        >
          <TrashIcon className="w-6" />
          {text}
        </div>
        {modal}
      </>
    );
  }

  return (
    <>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-delete-report"}
      />
      <TrashIcon
        className="w-6 text-red-500 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
        onClick={handleCancel}
        data-tip="Cancel Report"
        data-for="rt-delete-report"
      />
      {modal}
    </>
  );
};

export const OpenAmendmentAdminCompletedButton = ({
  reportUUID,
  text,
  close,
}: ReportUUIDProps) => {
  const navigate = useNavigate();
  const {
    isLoading: loadingAmendment,
    mutate: openAmendment,
    error: errorAmendment,
    isSuccess: isSuccessAmendment,
    reset,
  } = useReportAmendment();
  const { modal, show, hide } = useConfirmationDialog();

  function handleAmendment() {
    show(
      "Open Amendment",
      "Are you sure you want to open an amendment? Any changes made will overwrite the original report and cannot be undone.",
      (action: ConfirmDialogActions) => {
        if (action === "continue" && reportUUID) {
          openAmendment(reportUUID);
        }
        hide();
        if (action === "close") {
          close?.();
        }
      }
    );
  }

  useEffect(() => {
    if (errorAmendment) {
      show(
        "Error Removing the report:",
        "",
        () => {},
        <div className="my-2">
          <ErrorMessageContainer>
            <>
              <strong className="block py-1">{reportUUID}</strong>
              Please try again or contact the administrator.
            </>
          </ErrorMessageContainer>
        </div>,
        false
      );
      reset();
    }
  }, [errorAmendment, reportUUID, reset, show]);

  useEffect(() => {
    if (loadingAmendment) {
      show("", "", () => {}, <LoadingSpinner />, false);
    }
    if (isSuccessAmendment) {
      navigate(0);
    }
  }, [show, isSuccessAmendment, navigate, loadingAmendment]);

  if (text) {
    return (
      <>
        <AdminCompletedActionRow onClick={handleAmendment}>
          <ShieldExclamationIcon className="w-4" />
          {text}
        </AdminCompletedActionRow>
        {modal}
      </>
    );
  }

  return (
    <>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-open-amendment"}
      />
      <ShieldExclamationIcon
        className="w-6 text-gray-400 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
        onClick={handleAmendment}
        data-tip="Open Amendment"
        data-for="rt-open-amendment"
      />
      {modal}
    </>
  );
};

export function ReportLogAdminCompletedButton({
  reportUUID,
  text,
  close,
}: ReportUUIDProps) {
  const navigate = useNavigate();
  function handleReportLog() {
    navigate(`/reports/${reportUUID}/logs`);
    close?.();
  }

  if (text) {
    return (
      <AdminCompletedActionRow onClick={handleReportLog}>
        <ClockIcon className="w-4" />
        {text}
      </AdminCompletedActionRow>
    );
  }

  return (
    <div>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-report-log"}
      />
      <ClockIcon
        className="w-6 text-gray-400 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
        onClick={handleReportLog}
        data-tip="Report Log"
        data-for="rt-report-log"
      />
    </div>
  );
}

export function ReportRegeneratePDFAdminCompletedButton({
  reportUUID,
  text,
  close,
}: ReportUUIDProps) {
  const {
    regeneratePDF,
    loadingGeneratePDF,
    successGeneratePDF,
    errorGeneratePDF,
  } = useRegeneratePDFWithToast();
  const { modal, show, hide } = useConfirmationDialog();

  useEffect(() => {
    if (successGeneratePDF) {
      hide();
      close?.();
    }
  }, [successGeneratePDF, hide, close]);

  useEffect(() => {
    if (errorGeneratePDF) {
      hide();
      close?.();
    }
  }, [errorGeneratePDF, hide, close]);

  useEffect(() => {
    if (loadingGeneratePDF) {
      show("", "", () => {}, <LoadingSpinner />, false);
    }
  }, [loadingGeneratePDF, show]);

  function handleRegeneratePDF() {
    show(
      "Regenerate PDF",
      "Are you sure you want to regenerate the PDF?",
      (action: ConfirmDialogActions) => {
        if (action === "continue" && reportUUID) {
          hide();
          regeneratePDF(reportUUID);
        }
        if (action === "close") {
          hide();
          close?.();
        }
      }
    );
  }
  if (text) {
    return (
      <>
        {!loadingGeneratePDF && (
          <AdminCompletedActionRow onClick={handleRegeneratePDF}>
            <DocumentIcon className="w-4" />
            {text}
          </AdminCompletedActionRow>
        )}
        {loadingGeneratePDF && <LoadingSpinner className="w-4" />}
        {modal}
      </>
    );
  }
  return (
    <>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-regenerate-pdf"}
      />
      {!loadingGeneratePDF && (
        <PaperClipIcon
          className="w-6 text-gray-400 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
          onClick={handleRegeneratePDF}
          data-tip="Regenerate PDF"
          data-for="rt-regenerate-pdf"
        />
      )}
      {loadingGeneratePDF && <LoadingSpinner />}
      {modal}
    </>
  );
}

type ReportProps = {
  report: ReportSummaryItemApi;
  onChange?: () => void;
  close?: () => void;
  text?: string;
};

export function ReportRerunPersonPDFAdminCompletedButton({
  report,
  text,
  close,
}: ReportProps) {
  const { navigateCreatePerson } = useNavigateNewPerson();
  const { modal, show, hide } = useConfirmationDialog();

  function handleRerunPerson() {
    show(
      "Rerun Person",
      "Are you sure you want to rerun the report?",
      (action: ConfirmDialogActions) => {
        if (action === "continue" && report) {
          navigateCreatePerson({
            state: { rerun: true, parent_report: { ...report } },
          });
        }
        hide();
        close?.();
      }
    );
  }
  if (text) {
    return (
      <>
        <AdminCompletedActionRow onClick={handleRerunPerson}>
          <ArrowPathIcon className="w-4" />
          {text}
        </AdminCompletedActionRow>
        {modal}
      </>
    );
  }
  return (
    <>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-re-run-person"}
      />
      <ArrowPathIcon
        className="w-6 text-gray-400 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
        onClick={handleRerunPerson}
        data-tip="Rerun Person"
        data-for="rt-re-run-person"
      />
      {modal}
    </>
  );
}

export function ReportChangeDueDateAdmindButton({
  report,
  onChange,
}: ReportProps) {
  const [isDueDateModalOpen, setIsDueDateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const {
    mutate: updateWorkflowDueDate,
    error: getWorkflowDueDateError,
    isSuccess,
  } = useUpdateMisconductDueDate();
  const handleDueDateChange = async (newDueDate: Date) => {
    if (report) {
      setIsDueDateModalOpen(false);
      updateWorkflowDueDate({
        external_id: report.workflow_run_uuid,
        due_date: newDueDate,
      });
      setSelectedDate(newDueDate);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      onChange?.();
    }
  }, [isSuccess, onChange, selectedDate]);

  return (
    <>
      <Modal
        open={isDueDateModalOpen}
        onClose={() => setIsDueDateModalOpen(false)}
        title={"Change Due Date"}
        width={"w-full max-w-sm"}
      >
        {report && (
          <ChangeDueDateContainer
            selectedDate={selectedDate ?? new Date(report.due_date)}
            createdAt={new Date(report.created_at)}
            handleDueDateChange={handleDueDateChange}
          />
        )}
      </Modal>
      <ReactTooltip
        className="hover:visible:opacity-1 h-9"
        effect="solid"
        id={"rt-download-pdf"}
      />
      <CalendarIcon
        className="w-6 text-gray-400 border border-gray-300 shadow-sm rounded-md p-1 cursor-pointer"
        data-tip="Change Due Date"
        data-for="rt-download-pdf"
        onClick={() => setIsDueDateModalOpen(true)}
      />
      {getWorkflowDueDateError && (
        <ExclamationCircleIcon className="w-4 text-red-500" />
      )}
    </>
  );
}
